import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // For free gifts, disable minus button at quantity 1 (let trash icon handle removal)
    if (this.dataset.freeGift === 'true') {
      minusButton.disabled = value <= 1;
      // Plus button is never disabled for free gifts (adds paid variant instead)
      plusButton.disabled = false;
    } else {
      // Cart buttons are always dynamically managed
      minusButton.disabled = value <= min;
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Increases the quantity - intercepts free gift clicks
   * @override
   */
  async increaseQuantity(event) {
    // Check if this is a free gift variant
    if (this.dataset.freeGift === 'true') {
      const normalVariantId = this.dataset.normalVariantId;

      if (normalVariantId) {
        // Prevent default if event exists
        if (event) event.preventDefault();
        
        // Add paid variant instead of increasing free gift quantity
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              id: normalVariantId, 
              quantity: 1 
            })
          });
          
          if (response.ok) {
            // Trigger cart update event
            const cartData = await response.json();
            const cartUpdateEvent = new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cartData,
                sourceId: 'cart-quantity-selector-free-gift',
                data: {
                  itemCount: cartData.item_count
                }
              }
            });
            document.documentElement.dispatchEvent(cartUpdateEvent);
          }
        } catch (error) {
          console.error('Failed to add paid variant:', error);
        }
        
        // Don't increase the free gift quantity
        return;
      }
    }
    
    // Normal increase logic for non-free gifts - PASS EVENT TO PARENT
    super.increaseQuantity(event);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
